import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Mail, FileText, UserPlus, Plus, Loader2, Link2,
  Copy, Send, Eye, Trash2, Play,
} from 'lucide-react';
import {
  JURISDICTION_FLAGS, formatDate,
  getScoreColor, getScoreLevel, getInvitationStatusColor,
} from './adminUtils';
import { PublicLinkCard } from './PublicLinkCard';

export const InvitationsTab = ({
  invitations,
  guestAssessments,
  showNewInvitationForm,
  setShowNewInvitationForm,
  newInvitation,
  setNewInvitation,
  creatingInvitation,
  sendingInvitation,
  onCreate,
  onCopyLink,
  onSendEmail,
  onDelete,
  onViewGuest,
  onStartInPerson,
}) => {
  const completedGuests = guestAssessments.filter((a) => a.status === 'completed');

  return (
    <div className="space-y-6" data-testid="admin-invitations-tab">
      <PublicLinkCard />

      {/* Create Invitation Form */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[#3B82F6]" />
              Send Assessment Invitation
            </h3>
            <div className="flex gap-2">
              <Button
                data-testid="open-inperson-btn"
                onClick={onStartInPerson}
                variant="outline"
                className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
              >
                <Play className="h-4 w-4 mr-2" />
                In-Person Now
              </Button>
              <Button
                data-testid="toggle-new-invitation-form-btn"
                onClick={() => setShowNewInvitationForm(!showNewInvitationForm)}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Invitation
              </Button>
            </div>
          </div>

          {showNewInvitationForm && (
            <form onSubmit={onCreate} className="bg-slate-700/30 rounded-lg p-4 space-y-4" data-testid="new-invitation-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Email *</Label>
                  <Input
                    type="email"
                    data-testid="invitation-email-input"
                    value={newInvitation.email}
                    onChange={(e) => setNewInvitation((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="client@company.com"
                    required
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Jurisdiction *</Label>
                  <Select
                    value={newInvitation.jurisdiction}
                    onValueChange={(v) => setNewInvitation((prev) => ({ ...prev, jurisdiction: v }))}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="invitation-jurisdiction-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="jamaica">Jamaica</SelectItem>
                      <SelectItem value="barbados">Barbados</SelectItem>
                      <SelectItem value="bermuda">Bermuda</SelectItem>
                      <SelectItem value="cayman">Cayman Islands</SelectItem>
                      <SelectItem value="bahamas">The Bahamas</SelectItem>
                      <SelectItem value="belize">Belize</SelectItem>
                      <SelectItem value="grenada">Grenada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Contact Name</Label>
                  <Input
                    data-testid="invitation-name-input"
                    value={newInvitation.name}
                    onChange={(e) => setNewInvitation((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="John Smith"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Company</Label>
                  <Input
                    data-testid="invitation-company-input"
                    value={newInvitation.company_name}
                    onChange={(e) => setNewInvitation((prev) => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Acme Corp"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Industry</Label>
                  <Select
                    value={newInvitation.industry || ''}
                    onValueChange={(v) => setNewInvitation((prev) => ({ ...prev, industry: v }))}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="invitation-industry-select">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Financial Services">Financial Services</SelectItem>
                      <SelectItem value="Healthcare">Healthcare</SelectItem>
                      <SelectItem value="Telecoms">Telecoms</SelectItem>
                      <SelectItem value="Education">Education</SelectItem>
                      <SelectItem value="Retail">Retail</SelectItem>
                      <SelectItem value="Government">Government</SelectItem>
                      <SelectItem value="Hospitality">Hospitality</SelectItem>
                      <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="Professional Services">Professional Services</SelectItem>
                      <SelectItem value="Technology">Technology</SelectItem>
                      <SelectItem value="Non-profit">Non-profit</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Phone</Label>
                  <Input
                    data-testid="invitation-phone-input"
                    value={newInvitation.phone || ''}
                    onChange={(e) => setNewInvitation((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="+1-868-555-1234"
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Engagement Status</Label>
                  <Select
                    value={newInvitation.status || 'prospect'}
                    onValueChange={(v) => setNewInvitation((prev) => ({ ...prev, status: v }))}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="invitation-engagement-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="engaged">Engaged</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="recurring">Recurring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Link Expires In</Label>
                  <Select
                    value={String(newInvitation.expires_in_days || 14)}
                    onValueChange={(v) => setNewInvitation((prev) => ({ ...prev, expires_in_days: Number(v) }))}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="invitation-expiry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days (default)</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Internal Notes (private — not visible to client)</Label>
                <textarea
                  data-testid="invitation-notes-input"
                  value={newInvitation.internal_notes || ''}
                  onChange={(e) => setNewInvitation((prev) => ({ ...prev, internal_notes: e.target.value }))}
                  placeholder="Conversation history, engagement context, follow-up flags..."
                  rows={3}
                  className="w-full bg-slate-700 border border-slate-600 text-white mt-1 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  data-testid="create-invitation-btn"
                  disabled={creatingInvitation}
                  className="bg-[#10B981] hover:bg-[#059669] text-white"
                >
                  {creatingInvitation ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Link2 className="h-4 w-4 mr-2" />
                  )}
                  Create & Copy Link
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowNewInvitationForm(false)}
                  className="border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Invitations List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#3B82F6]" />
            All Invitations ({invitations.length})
          </h3>

          {invitations.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No invitations yet. Create one to get started.</p>
          ) : (
            <div className="space-y-3">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <span className="text-2xl">{JURISDICTION_FLAGS[invitation.jurisdiction]}</span>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-medium">{invitation.name || invitation.email}</p>
                        <Badge className={`text-xs ${getInvitationStatusColor(invitation.status)}`}>
                          {invitation.status.toUpperCase()}
                        </Badge>
                        {invitation.self_requested && (
                          <Badge className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">
                            Self-requested
                          </Badge>
                        )}
                      </div>
                      <p className="text-slate-500 text-sm">
                        {invitation.company_name || 'No company'} • {invitation.email}
                      </p>
                      <p className="text-slate-600 text-xs">
                        Created: {formatDate(invitation.created_at)}
                        {invitation.status === 'completed' && ` • Completed: ${formatDate(invitation.completed_at)}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`copy-invitation-${invitation.id}`}
                      onClick={() => onCopyLink(invitation.invitation_link)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      title="Copy Link"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`send-invitation-${invitation.id}`}
                      onClick={() => onSendEmail(invitation.id)}
                      disabled={sendingInvitation === invitation.id || invitation.status === 'completed'}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                      title="Send Email"
                    >
                      {sendingInvitation === invitation.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                    {invitation.status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`view-guest-result-${invitation.id}`}
                        onClick={() => {
                          const ga = guestAssessments.find((a) => a.invitation_id === invitation.id);
                          if (ga) onViewGuest(ga);
                        }}
                        className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
                        title="View Results"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`delete-invitation-${invitation.id}`}
                      onClick={() => onDelete(invitation.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completed Guest Assessments */}
      {completedGuests.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#10B981]" />
              Completed Guest Assessments ({completedGuests.length})
            </h3>
            <div className="space-y-3">
              {completedGuests.map((assessment) => (
                <div
                  key={assessment.id}
                  className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors cursor-pointer"
                  onClick={() => onViewGuest(assessment)}
                  data-testid={`guest-assessment-${assessment.id}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl">{JURISDICTION_FLAGS[assessment.jurisdiction]}</span>
                    <div>
                      <p className="text-white font-medium">{assessment.guest_info?.name || 'Unknown'}</p>
                      <p className="text-slate-500 text-sm">
                        {assessment.guest_info?.company_name} • {assessment.guest_info?.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={getScoreColor(getScoreLevel(assessment.internal_scores?.overall_score || 0))}>
                      {(assessment.internal_scores?.overall_score || 0).toFixed(0)}%
                    </Badge>
                    <Badge className="bg-slate-600 text-slate-300">
                      {assessment.internal_scores?.overall_rating?.rating || 'N/A'}
                    </Badge>
                    <Eye className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
